
export { cn } from './cn';

/** This file contains utility  */
/** Settings path to the folder containing database images */
const databaseImagePath : string = "/static/database/images/";
const uploadsPath : string = "/static/uploads/";

/** Image variant sizes available from the upload pipeline */
export type ImageVariant = 'thumb' | 'medium' | 'large';

/** Utility function to transform a given image src specified in the database to the correct url.
 *  Handles both legacy paths (from /static/database/images/) and new upload paths (from /static/uploads/).
 *  Pass a variant to request a thumbnail size (thumb=200px, medium=600px, large=1200px).
 */
export function GetImageUrl(/** The relative image name with extension */ src: string, /** Optional absolut url prefix */ url? : string, /** Optional variant size */ variant?: ImageVariant) {
	if (!src || typeof src !== 'string') return '';
	// New upload paths already include the subdir (e.g. "images/categories/...")
	const base = isNewUploadPath(src) ? uploadsPath : databaseImagePath;
	let resolvedSrc = variant && isNewUploadPath(src) ? insertVariantSuffix(src, variant) : src;
	// Legacy images: prefer WebP copy (generated by migrate-legacy-webp)
	if (!isNewUploadPath(src)) {
		resolvedSrc = resolvedSrc.replace(/\.(png|jpg|jpeg)$/i, '.webp');
	}
	return (url && url.length ? url + base + resolvedSrc : base + resolvedSrc);
}

/** Insert variant suffix before file extension: "images/foo.webp" → "images/foo-medium.webp" */
function insertVariantSuffix(src: string, variant: string): string {
	const dotIdx = src.lastIndexOf('.');
	if (dotIdx === -1) return src;
	return `${src.slice(0, dotIdx)}-${variant}${src.slice(dotIdx)}`;
}

/** Returns the full URL for a new upload relative path (from /static/uploads/) */
export function GetUploadUrl(relativePath: string): string {
	return uploadsPath + relativePath;
}

/** Check if a path is from the new upload system (starts with images/, profiles/, docs/) */
export function isNewUploadPath(src: string): boolean {
	return src.startsWith('images/') || src.startsWith('profiles/') || src.startsWith('docs/');
}

/** Pixel widths matching the server-side VARIANT_SIZES + primary dimension */
const SRCSET_WIDTHS: Record<ImageVariant, number> = {
	thumb: 200,
	medium: 600,
	large: 1200,
};
const PRIMARY_WIDTH = 1920;

/** Swap file extension: "images/foo.webp" → "images/foo.avif" */
function swapExtension(src: string, newExt: string): string {
	const dotIdx = src.lastIndexOf('.');
	if (dotIdx === -1) return src;
	return `${src.slice(0, dotIdx)}.${newExt}`;
}

/**
 * Get the LQIP (Low-Quality Image Placeholder) URL for a new upload path.
 * Returns undefined for legacy paths or invalid inputs.
 */
export function getLqipUrl(src: string): string | undefined {
	if (!src || !isNewUploadPath(src)) return undefined;
	return `${uploadsPath}${insertVariantSuffix(src, 'lqip')}`;
}

/**
 * Build a srcset string from the variant system for new upload paths.
 * Returns e.g. "/static/uploads/images/foo-thumb.avif 200w, .../foo-medium.avif 600w, ..."
 * Only works for new upload paths — returns undefined for legacy paths.
 */
export function buildSrcSet(src: string, format?: 'avif' | 'webp'): string | undefined {
	if (!src || !isNewUploadPath(src)) return undefined;

	const resolvedSrc = format ? swapExtension(src, format) : src;
	const entries: string[] = [];

	for (const [name, width] of Object.entries(SRCSET_WIDTHS)) {
		const variantSrc = insertVariantSuffix(resolvedSrc, name);
		entries.push(`${uploadsPath}${variantSrc} ${width}w`);
	}
	// Add the primary (full-size) image
	entries.push(`${uploadsPath}${resolvedSrc} ${PRIMARY_WIDTH}w`);

	return entries.join(', ');
}

/* Is a eventfunction that make sure you hold the target pressed for given amount of milliseconds */
export function onHold(callback: () => void, time: number = 1000) {
    const timer = setTimeout(() => {
        callback()
    }, time);

    return () => {
        return clearTimeout(timer)
    }
}

type BuildingChain = [IBuilding, IFacade, IFloor] | [IBuilding, IRoof]

export const ErrBNotFound = new Error('OrderItem not found in chain')

function iter_stop<T>(iterable: Array<T>, cb: (t: T, i: number) => boolean) {
  for(const [i, v] of iterable.entries()) {
    if (cb(v, i)) return true
  }
  return false
}

/**
 * Gets the chain of building components that a order item
 * belogs to.
 * Throws ErrBNotFound if a valid chain could not be founc.
 * @throws {ErrBNotFound}
 */
export function GetBuildingChainFromOrderItem(project: IProject, item: IOrderItem) : BuildingChain {
  let path = ""
  iter_stop(project.buildings, (b, bi) => {
    let stop = iter_stop(b.roofs, (r, ri) => {
      const i = r.cart.find(i => i._id == item._id)
      if (i) {
        path = `${bi};${ri}`
        return true
      }
      return false
    })
    if (stop) return true
    return iter_stop(b.facades, (f, fi) => {
      return iter_stop(f.floors, (ff, ffi) => {
        const i = ff.cart.find(i => i._id == item._id)
        if (i) {
          path = `${bi};${fi};${ffi}`
          return true
        }
        return false
      })
    })
  })
  let b,r,f,ff
  const parts = path.split(";")
  switch (parts.length) {
    case 2:
      b = project.buildings[Number(parts[0])]
      r = b.roofs[Number(parts[1])]
      return [ b, r ]
    case 3:
      b = project.buildings[Number(parts[0])]
      f = b.facades[Number(parts[1])]
      ff = f.floors[Number(parts[2])]
      return [b,f,ff]
    default:
      throw ErrBNotFound
  }
}

/**
 * Returns a string representation of the building chain
 * that order item belongs to.
 * Throws ErrBNotFound if a valid chain could not be founc.
 * @throws {ErrBNotFound}
 */
export function RenderBuildingChain(project: IProject, item: IOrderItem): string {
    const chain: {name: string}[] = GetBuildingChainFromOrderItem(project, item)
    return chain.map(link => link.name).join(", ")
}

export function generateUUID(length = 4, options = { numericOnly: false }) {
  let text = '';
  const possible =
    options && options.numericOnly ? '0123456789' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}